import React, {useEffect, useState, useTransition} from 'react';
import {
    AppInterface,
    EventReminderButton,
    EventReminderPanel,
    EventTypeToggle,
    InternalPageHeaderBar,
    RecordsPagination,
    Survey_AddEdit_Page,
    Survey_InternalPage,
    Survey_ViewPage,
    Survey_ViewQsPage,
    ThemedButton,
    ThemedConfirmDialog,
    ThemedIconButton
} from "../../index";
import {EventTypes, GenRouteUrls, Keys, ModuleFieldsData, Modules, ReqUrls, ShowViewType} from "../../config/AppConfig";
import {useTheme} from "../../../common/ProjectThemeProvider";
import {
    checkNullArr,
    checkNullStr,
    downloadExcelFile,
    extractHtmlInnerText,
    getArrLen,
    getJsonValue,
    getHighlightedMatchedText,
    getJsonValueFromNestedKeys,
    isBoolTrue,
    isDateExpired,
    isJsonValueTrue,
    limitStringWords,
    shuffleList,
    sortJsonListAdvance
} from "../../../common/fusion-ui/utils/ReactUtils";
import {formatDateValue, getSubdomainSessionField, isCurrUserRoleAdmin, printError, printLog, sendRequest} from "../../utils/AppUtils";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {Anchor, Flexbox, Label, LoadingSection, NullMsgLabel, Section} from "../../../common/fusion-ui"
import {CssVariant, DefStrNA, Messages} from 'common/fusion-ui/config/FusionConfig';
import {useReduxPersistSessionData} from "../../hook/useReduxPersistSessionData";
import {useConfig} from "../../context/ConfigProvider";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";
import {Tooltip} from "@mui/material";
import {AnimationType, FramerAnimator} from "../../../common/fusion-ui/animation/FramerAnimator";

const SurveyPage = React.memo(({
                                   fromSearchPage = false,
                                   recordList = [],
                                   loadingStatus = {},
                                   onRecordsUpdate,
                                   showControls = true,
                                   showHeader = true,
                                   showPagination = true,
                                   searchString,
                               }) => {
    const TAG = "SurveyPage";

    const moduleName = Modules.Survey;
    const mf = ModuleFieldsData[moduleName];

    const animator = new FramerAnimator();

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isConfigTrue, updateConfig} = useConfig();
    const {isScreenUpSm, isScreenXs, isScreenSm} = useScreenSize();

    const SEARCH_BY_PARAMS = {
        anything: {name: 'Anything'},
        title: {name: mf.title.title},
        introduction: {name: mf.introduction.title},
        date: {name: mf.endDate.title},
    };
    const SORT_BY_PARAMS = {
        title: {
            name: mf.title.title,
            sortKey: mf.title.column,
        },
        introduction: {
            name: mf.introduction.title,
            sortKey: mf.introduction.column,
        },
        date: {
            name: mf.endDate.title,
            sortKey: mf.endDate.column,
        },
    };
    const PAGINATION_RECORD_LIMITS = [9, 12, 15, 18];
    const DEF_PGNTN_RECORD_LIMIT = PAGINATION_RECORD_LIMITS[0];
    const DEF_PGNTN_START_INDEX = 1;
    const INTRO_WORD_LIMIT = 40;

    const Fields = {
        recordList: `${TAG}_recordList`,
        summaryList: `${TAG}_summaryList`,

        isDataLoading: 'isDataLoading',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFound: 'isDataFound',

        recordDeleteBtn_Disable: 'recordDeleteBtn_Disable',
        recordDeleteBtn_AsLoading: 'recordDeleteBtn_AsLoading',
        showDeleteDialog: 'showDeleteDialog',

        recordDlReportBtn_Disable: 'recordDlReportBtn_Disable',
        recordDlReportBtn_AsLoading: 'recordDlReportBtn_AsLoading',

        currRecordId_internal: 'currRecordId_internal',
        currRecordId_editForm: 'currRecordId_editForm',
        currRecordId_view: 'currRecordId_view',
        currRecordId_delete: 'currRecordId_delete',
        currRecordId_ViewQs: 'currRecordId_ViewQs',
        currRecordId_DlReport: 'currRecordId_DlReport',

        currEventTypeId: 'currEventTypeId',

        showInternalPopup: 'showInternalPopup',
        showEditFormPopup: 'showEditFormPopup',
        showViewSurveyPopup: 'showViewSurveyPopup',
        showViewQsPopup: 'showViewQsPopup',

        openEventReminderPopup: 'openEventReminderPopup',
        recordId_eventReminder: 'recordId_eventReminder',

        searchInputValue: 'searchInputValue',
        searchBySelectedOption: 'searchBySelectedOption',
        sortBySelectedOption: 'sortBySelectedOption',
        isSortByOrderAsc: 'isSortByOrderAsc',

        pagination_currPageNum: 'pagination_currPageNum',
        pagination_currPageLimit: 'pagination_currPageLimit',
    };
    const [fieldsData, setFieldsData] = useState({
        [Fields.isDataLoading]: true,
    });

    const {sessionData, updateSessionStoreFieldValue, getSessionStoreFieldValue} = useReduxPersistSessionData({
        keys: [Fields.recordList, Fields.summaryList,],
    });

    const [sortListPending, startSortListTransition] = useTransition();
    const [searchListPending, startSearchListTransition] = useTransition();

    useEffect(() => {
        if (!isBoolTrue(fromSearchPage)) {
            updateConfig(Keys.config_ShowTabBar, true);
            updateConfig(Keys.config_TabBarTab, moduleName);
            updateConfig(Keys.tracker_currPageModule, moduleName);
            updateConfig(Keys.tracker_currPageTag, TAG);
        }
    }, [fromSearchPage]);

    useEffect(() => {
        if (!checkNullStr(getField(Fields.pagination_currPageNum))) {
            updateField(Fields.searchBySelectedOption, SEARCH_BY_PARAMS.anything.name);
            updateField(Fields.sortBySelectedOption, SORT_BY_PARAMS.title.name);
            updateField(Fields.isSortByOrderAsc, true);
            updateField(Fields.pagination_currPageNum, DEF_PGNTN_START_INDEX);
            updateField(Fields.pagination_currPageLimit, DEF_PGNTN_RECORD_LIMIT);
            updateField(Fields.currEventTypeId, EventTypes.all.id);
        }
        if (isBoolTrue(fromSearchPage)) {
            if (!isFieldTrue(Fields.isDataFetched)) {
                updateField(Fields.isDataFetched, true);
                const success = checkNullArr(recordList);
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, success);
                updateField(Fields.dataLoadingMsg, success ? '' : Messages.noDataFound);
                // log('useEffect.recordList:', recordList, 'found:', checkNullArr(recordList));

                if (success) {
                    // sortJsonListAdvance(recordList, SORT_BY_PARAMS.title.sortKey);
                    updateSessionStoreFieldValue(Fields.recordList, recordList);
                }
            }
        } else {
            fetchPageData();
        }
    }, [sessionData, recordList, loadingStatus, searchString, fromSearchPage]);

    return (<>
        {isBoolTrue(fromSearchPage) ? createRecordListPanel() : createPage()}
        {isBoolTrue(showControls) && createDeleteDialog()}
        {createViewPanelPopup()}
        {createViewQsPopup()}
        {createInternalPanelPopup()}
        {isCurrUserRoleAdmin() && createEditFormPopup()}
        {isCurrUserRoleAdmin() && createEventReminderPopup()}
    </>);

    function createPage() {
        return <>
            <AppInterface
                label={'My pending and incomplete surveys'}>
                <div className={''}>
                    {<LoadingSection
                        labelClassName={'m-2 w-100 fs-normal'}
                        heightClass={75}
                        color={theme.text70}
                        success={isFieldTrue(Fields.isDataFound)}
                        loading={isFieldTrue(Fields.isDataLoading)}
                        msg={getField(Fields.dataLoadingMsg)}
                    />}

                    {(!isFieldTrue(Fields.isDataLoading) && isFieldTrue(Fields.isDataFound)) &&
                        <div className={`m-0 p-0`}>
                            {createRecordListPanel()}

                            {/* Summary list */}
                            {<div className={'mt-3 mb-2'}>
                                <Label
                                    className={'m-0 p-0 px-2 fs-lg fw-bold'}
                                    color={theme.primary}
                                    textAt={'start'}>
                                    {moduleName} Summary
                                </Label>

                                {/*links*/}
                                {checkNullArr(getSummaryList())
                                    ? <div>{getSummaryList().map((item, index) => {
                                        return (checkNullStr(getJsonValue(item, 'name')) && checkNullStr(getJsonValue(item, 'link')))
                                            && <div key={index + getJsonValue(item, 'name')}
                                                    className={"border rounded-3 p-1 mb-2"}
                                                    onMouseEnter={(e) => e.target.style.boxShadow = theme.boxShadowXs}
                                                    onMouseLeave={(e) => e.target.style.boxShadow = 'none'}>
                                                <Anchor
                                                    className={'fw-semi-boldx m-0 px-1'}
                                                    textAt={'start'}
                                                    href={getJsonValue(item, 'link')}
                                                    target={'_blank'}
                                                    simple={true}>
                                                    {limitStringWords(getJsonValue(item, 'name'), 30, " ", '...')}
                                                </Anchor>
                                            </div>
                                    })}
                                    </div>
                                    : <NullMsgLabel textAt={'start'} className={'px-2'} color={theme.text70}>No data found</NullMsgLabel>
                                }
                            </div>}
                        </div>}
                </div>
            </AppInterface>
        </>
    }

    function createRecordListPanel() {
        return <div>
            <div className={''}>
                {/*header-bar*/}
                {isBoolTrue(showHeader) && <InternalPageHeaderBar
                    moduleName={moduleName}
                    pos={''}
                    justifyAt={'between'}
                    bg={'transparent'}
                    px={0}
                    py={0}
                    pt={0}

                    searchByParams={SEARCH_BY_PARAMS}
                    sortByParams={SORT_BY_PARAMS}
                    showSearch={!isBoolTrue(fromSearchPage)}

                    recordsLen={getArrLen(getRecordList(true))}
                    filteredRecordsLen={getArrLen(getRecordList(true, true))}
                    searchValue={getField(Fields.searchInputValue)}
                    searchByDefOption={getField(Fields.searchBySelectedOption)}
                    sortByDefOption={getField(Fields.sortBySelectedOption)}
                    isSorting={sortListPending}
                    isSearching={searchListPending}
                    isSortAsc={isFieldTrue(Fields.isSortByOrderAsc)}

                    onSearchChange={(e, value) => handleSearchInputChange(value)}
                    onSearchBy={(name, item, index) => {
                        updateField(Fields.searchBySelectedOption, name);
                        handleSearchInputChange(getField(Fields.searchInputValue), name);
                    }}
                    onSort={(name, item, index, sortKey) => {
                        handleSortByChange(name, sortKey);
                    }}

                    leftComponent={<EventTypeToggle onSwitch={(id) => updateField(Fields.currEventTypeId, id)}/>}
                />}


                {/* record-list */}
                <div className={'m-0 p-0 w-100 mt-1'}>
                    {animator.setAnimationType(AnimationType.SLIDE_UP).renderChildren(
                        <div className={'w-100 m-0 p-0'}>
                            {checkNullArr(getRecordListToRender())
                                ? getRecordListToRender().map((item, index) => {
                                    const recordId = getJsonValue(item, mf.recordId.column)
                                    const eventStatusIndicator = getEventStatusIndicatorTag(item);
                                    return <Section
                                        key={index + getJsonValue(item, mf.title.column)}
                                        className={'m-0 p-2 h-100 mb-3 app-section hover'}
                                        position={'relative'}
                                        borderless={false}
                                        roundedVariant={4}
                                        borderColor={theme.text20}
                                        contentClassName={'px-1'}>
                                        {/*title-link*/}
                                        <Flexbox className={''} justifyAt={'between'} alignAt={'start'}>
                                            {/*title*/}
                                            <div className={'d-flex w-auto'}>
                                                <Anchor
                                                    wrapperClassName={'w-auto'}
                                                    className={'fw-semi-bold text-wrap'}
                                                    href={`${GenRouteUrls.survey_internalPage}${recordId}`}
                                                    onLinkClick={(e) => {
                                                        if (!isScreenUpSm) {
                                                            e.preventDefault();
                                                            if (e.ctrlKey) {
                                                                window.open(`${GenRouteUrls.survey_internalPage}${recordId}`, '_blank');
                                                            } else {
                                                                updateField(Fields.currRecordId_internal, recordId);
                                                                updateField(Fields.showInternalPopup, true);
                                                            }
                                                        }
                                                    }}>
                                                    {highlightSearchMatchedText((getJsonValue(item, mf.title.column)), SEARCH_BY_PARAMS.title.name)}
                                                </Anchor>
                                            </div>

                                            {/*event-status tag*/}
                                            {checkNullStr(eventStatusIndicator.label) &&
                                                <Label className={'ms-1 px-2 py-0 select-none d-flex al-center fs-md-sm fw-bold rounded-10'}
                                                       bgColor={eventStatusIndicator.bg}
                                                       color={eventStatusIndicator.color}
                                                       height={'20px'}>
                                                    {eventStatusIndicator.label}
                                                    {/*<span> | {getJsonValue(item, mf.date.column)}</span>*/}
                                                </Label>}
                                        </Flexbox>

                                        {/*introduction*/}
                                        <div>
                                            <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                <span className={'fw-semi-bold'}>{mf.introduction.title}: </span>
                                                <span>{" "}
                                                    {highlightSearchMatchedText(limitStringWords(extractHtmlInnerText(getJsonValue(item, mf.introduction.column)), INTRO_WORD_LIMIT, " ", '...'), SEARCH_BY_PARAMS.introduction.name)}
                                            </span>
                                            </Label>
                                        </div>


                                        {/*endDate & controls*/}
                                        <Flexbox className={''} justifyAt={'between'} justifyAtMd={'start'}
                                                 wrap={'wrap'}>
                                            {/*endDate*/}
                                            <Label className={'m-0 p-0 text-nowrap'} color={theme.text} textAt={'start'}>
                                                <span className={'fw-semi-bold'}>{mf.endDate.title}: </span>
                                                <span className={'text-nowrap'}>{" "}
                                                    {highlightSearchMatchedText(formatDateValue(getJsonValue(item, mf.endDate.column, DefStrNA)), SEARCH_BY_PARAMS.date.name)}
                                            </span>
                                            </Label>

                                            {/*controls*/}
                                            {isBoolTrue(showControls) && <Flexbox className={''}
                                                                                  justifyAt={'start'} justifyAtSm={'start'}
                                                                                  alignAt={'center'} alignAtSm={'start'}
                                                                                  wrapSm={'wrap'} directionSm={'column'}>
                                                <div className={'w-100 d-flex jc-start al-start fd-row flex-nowrap flex-wrap-sm'}>
                                                    {/* View Survey btn */}
                                                    <ThemedButton
                                                        className={'px-2 text-nowrap fs-sm'}
                                                        mr={1}
                                                        mb={1}
                                                        mt={1}
                                                        color={theme.primaryBlue}
                                                        outlined={true}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={theme.primaryBlue}
                                                        borderColor={theme.primaryBlue}
                                                        height={'22px'}
                                                        text={'View Survey'}
                                                        href={`${GenRouteUrls.survey_viewSurveyPage}${recordId}`}
                                                        loadingWidth={'auto'}
                                                        loadingVariant={CssVariant.primary}
                                                        onClick={(e) => {
                                                            if (!isScreenUpSm) {
                                                                e.preventDefault();
                                                                if (e.ctrlKey) {
                                                                    window.open(`${GenRouteUrls.survey_viewSurveyPage}${recordId}`, '_blank');
                                                                } else {
                                                                    updateField(Fields.currRecordId_view, recordId);
                                                                    updateField(Fields.showViewSurveyPopup, true);
                                                                }
                                                            }
                                                        }}
                                                    />

                                                    {/* Download Reports btn */}
                                                    {isCurrUserRoleAdmin() && <div style={{margin: '.23rem .65rem 0 .65rem'}}>
                                                        <ThemedButton
                                                            className={'px-2 text-nowrap fs-sm'}
                                                            color={theme.primaryBlue}
                                                            outlined={true}
                                                            bgColor={theme.textWhite}
                                                            hoverBgColor={theme.primaryBlue}
                                                            borderColor={theme.primaryBlue}
                                                            height={'22px'}
                                                            text={'Download Reports'}
                                                            loadingWidth={'auto'}
                                                            loadingVariant={CssVariant.warning}
                                                            asLoading={isFieldTrue(Fields.recordDlReportBtn_AsLoading) && getField(Fields.currRecordId_DlReport) == recordId}
                                                            disabled={isFieldTrue(Fields.recordDlReportBtn_Disable) && getField(Fields.currRecordId_DlReport) == recordId}
                                                            onClick={() => handleDownloadRecordReport(recordId)}
                                                        />
                                                    </div>}

                                                    {/* Add/Edit Questions btn */}
                                                    {isCurrUserRoleAdmin() && <ThemedButton
                                                        className={'px-2 text-nowrap fs-sm'}
                                                        m={1}
                                                        color={theme.primaryBlue}
                                                        outlined={true}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={theme.primaryBlue}
                                                        borderColor={theme.primaryBlue}
                                                        height={'22px'}
                                                        text={'Add/Edit Questions'}
                                                        href={`${GenRouteUrls.survey_viewQsPage}${recordId}`}
                                                        onClick={(e) => {
                                                            if (!isScreenUpSm) {
                                                                e.preventDefault();
                                                                if (e.ctrlKey) {
                                                                    window.open(`${GenRouteUrls.survey_viewQsPage}${recordId}`, '_blank');
                                                                } else {
                                                                    updateField(Fields.currRecordId_ViewQs, recordId);
                                                                    updateField(Fields.showViewQsPopup, true);
                                                                }
                                                            }
                                                        }}
                                                    />}
                                                </div>

                                                {isCurrUserRoleAdmin() &&
                                                    <Flexbox className={'w-100'} justifyAt={'start'} alignAt={'start'}>
                                                        {/* reminder send-btn */}
                                                        <Tooltip title={isEventExpired(item) ? `Reminder can't be sent for a past event.` : ''}>
                                                            <div>
                                                                {isCurrUserRoleAdmin() && <EventReminderButton
                                                                    circular={true}
                                                                    m={1} mr={2}
                                                                    size={'26px'} iconSize={'16px'}
                                                                    disabled={isEventExpired(item)}
                                                                    onClick={() => {
                                                                        updateField(Fields.openEventReminderPopup, true);
                                                                        updateField(Fields.recordId_eventReminder, recordId);
                                                                    }}
                                                                />}
                                                            </div>
                                                        </Tooltip>


                                                        {/* edit-btn */}
                                                        {/* Updated by vandit tewari (29/10/24) - change value of margin top from .1 to .2 just to adjust the icons */}
                                                        <div style={{marginTop: '.2rem'}}>
                                                            <ThemedIconButton
                                                                className={''}
                                                                tooltip={'Edit'}
                                                                m={1}
                                                                mx={2}
                                                                color={theme.primaryBlue}
                                                                bgColor={theme.textWhite}
                                                                hoverBgColor={theme.primaryBlue}
                                                                borderColor={theme.primaryBlue}
                                                                outlined={true}
                                                                icon={'bi bi bi-pencil'}
                                                                iconSize={'11px'}
                                                                size={'26px'}
                                                                href={`${GenRouteUrls.survey_editPage}${recordId}`}
                                                                onClick={(e) => {
                                                                    if (!isScreenUpSm) {
                                                                        e.preventDefault();
                                                                        if (e.ctrlKey) {
                                                                            window.open(`${GenRouteUrls.survey_editPage}${recordId}`, '_blank');
                                                                        } else {
                                                                            updateField(Fields.currRecordId_editForm, recordId);
                                                                            updateField(Fields.showEditFormPopup, true);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </div>

                                                        {/* delete-btn */}
                                                        <div>
                                                            <ThemedIconButton
                                                                className={''}
                                                                tooltip={'Delete'}
                                                                m={1}
                                                                mx={2}
                                                                variant={'red'}
                                                                bgColor={theme.textWhite}
                                                                hoverBgColor={'red'}
                                                                outlined={true}
                                                                icon={'bi bi bi-trash'}
                                                                iconSize={'11px'}
                                                                size={'26px'}
                                                                loadingVariant={CssVariant.danger}
                                                                asLoading={isFieldTrue(Fields.recordDeleteBtn_AsLoading) && getField(Fields.currRecordId_delete) == recordId}
                                                                disabled={isFieldTrue(Fields.recordDeleteBtn_Disable) && getField(Fields.currRecordId_delete) == recordId}
                                                                onClick={() => {
                                                                    updateField(Fields.currRecordId_delete, recordId);
                                                                    updateField(Fields.showDeleteDialog, true);
                                                                }}
                                                            />
                                                        </div>
                                                    </Flexbox>}
                                            </Flexbox>}
                                        </Flexbox>
                                    </Section>
                                })
                                : <NullMsgLabel className={'fw-semi-bold'} color={theme.text70} textAt={'start'}>
                                    {isSearchOngoing() ? 'Search not found!' : 'No records found!'}
                                </NullMsgLabel>}
                        </div>
                    )}
                </div>


                {/* pagination */}
                <RecordsPagination
                    id={TAG}
                    totalRecords={getArrLen(getRecordList(true, true))}
                    totalRecordsToRender={getArrLen(getRecordListToRender())}
                    recordLimits={PAGINATION_RECORD_LIMITS}
                    onPageChange={(e, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                    onLimitChange={(newLimit, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newLimit:', newLimit, 'newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageLimit, newLimit);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                />
            </div>
        </div>
    }

    function createEventReminderPopup() {
        return <EventReminderPanel
            moduleName={moduleName}
            recordId={getField(Fields.recordId_eventReminder)}
            recordUrl={`${GenRouteUrls.survey_internalPage}${getField(Fields.recordId_eventReminder)}`}
            open={isFieldTrue(Fields.openEventReminderPopup)}
            onDoneAndHide={() => {
                updateField(Fields.openEventReminderPopup, false);
                updateField(Fields.recordId_eventReminder, null);
            }}
        />
    }

    function createDeleteDialog() {
        return <ThemedConfirmDialog
            open={isFieldTrue(Fields.showDeleteDialog)}
            onCancel={() => updateField(Fields.showDeleteDialog, false)}
            onHide={() => updateField(Fields.currRecordId_delete, null)}
            title={`Delete ${moduleName}`}
            subMsg={getJsonValue(getRecordList().find(item => getField(Fields.currRecordId_delete) == getJsonValue(item, mf.recordId.column)), mf.title.column)}
            operation={'delete'}
            target={'this survey topic'}
            onConfirm={() => {
                handleRecordDelete();
            }}
        />
    }

    function createInternalPanelPopup() {
        return <Survey_InternalPage
            showAs={ShowViewType.DRAWER}
            recordId={getField(Fields.currRecordId_internal)}
            open={isFieldTrue(Fields.showInternalPopup)}
            onHide={() => {
                updateField(Fields.showInternalPopup, false);
                updateField(Fields.currRecordId_internal, null);
            }}
        />
    }

    function createEditFormPopup() {
        return <Survey_AddEdit_Page
            showAs={ShowViewType.DRAWER}
            updateOnly={true}
            recordId={getField(Fields.currRecordId_editForm)}
            open={isFieldTrue(Fields.showEditFormPopup)}
            onSuccess={() => {
                updateField(Fields.showEditFormPopup, false);
                updateField(Fields.currRecordId_editForm, null);
                fetchPageData(false);
            }}
            onHide={() => {
                updateField(Fields.showEditFormPopup, false);
                updateField(Fields.currRecordId_editForm, null);
            }}
        />
    }

    function createViewPanelPopup() {
        return <Survey_ViewPage
            showAs={ShowViewType.DRAWER}
            recordId={getField(Fields.currRecordId_view)}
            open={isFieldTrue(Fields.showViewSurveyPopup)}
            onSuccess={() => {
                updateField(Fields.showViewSurveyPopup, false);
                updateField(Fields.currRecordId_view, null);
            }}
            onHide={() => {
                updateField(Fields.showViewSurveyPopup, false);
                updateField(Fields.currRecordId_view, null);
            }}
        />
    }

    function createViewQsPopup() {
        return <Survey_ViewQsPage
            showAs={ShowViewType.DRAWER}
            recordId={getField(Fields.currRecordId_ViewQs)}
            open={isFieldTrue(Fields.showViewQsPopup)}
            onHide={() => {
                updateField(Fields.showViewQsPopup, false);
                updateField(Fields.currRecordId_ViewQs, null);
            }}
        />
    }

    //======================Request Functions start=====================
    async function fetchPageData(checkBefore = true) {
        const fun = 'fetchPageData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            updateField(Fields.isDataFetched, true);
            try {
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, true);
                const sessionDataFound = checkBefore ? checkNullArr(getJsonValue(sessionData, Fields.recordList)) : false;
                log(fun, 'sessionDataFound:', sessionDataFound);
                if (sessionDataFound) {
                    updateField(Fields.isDataFound, true);
                    updateField(Fields.isDataLoading, false);
                } else {
                    //send-request
                    const REQUEST_OPTIONS = {};
                    sendRequest({
                        reqUrl: ReqUrls.survey__mainPage_fetch_recordList,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);

                            let recordList = getJsonValueFromNestedKeys(fetchedData, ['data', 'recordList'], []);
                            const summaryList = getJsonValueFromNestedKeys(fetchedData, ['data', 'summaryList'], []);
                            // log(fun, 'fetchedData.recordList:', recordList, 'summaryList:', summaryList);

                            const success = checkNullArr(recordList);
                            updateField(Fields.isDataFound, success);
                            if (success) {
                                // toast(Messages.fetchSuccess, CssVariant.success);success)

                                //Extract plain-text from introduction-items
                                recordList = recordList.map((item) => {
                                    return {...item, [mf.introduction.column]: extractHtmlInnerText(getJsonValue(item, mf.introduction.column))}
                                })

                                //Sort list by title by-default
                                // sortJsonListAdvance(recordList, SORT_BY_PARAMS.title.sortKey);

                                updateSessionStoreFieldValue(Fields.recordList, recordList);
                                updateSessionStoreFieldValue(Fields.summaryList, summaryList);
                            } else {
                                updateField(Fields.dataLoadingMsg, Messages.noDataFound);
                                toast(Messages.noDataFound, CssVariant.danger);
                            }
                            updateField(Fields.isDataLoading, false);
                        }),
                        onError: ((error) => {
                            logErr(fun, error);
                            updateField(Fields.isDataLoading, false);
                            updateField(Fields.isDataFound, false);
                            updateField(Fields.dataLoadingMsg, Messages.fetchError);
                        }),
                    });
                }
            } catch (error) {
                logErr(fun, error);
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, false);
                updateField(Fields.dataLoadingMsg, Messages.fetchError);
            }
        }
    }

    function handleRecordDelete() {
        const fun = 'handleRecordDelete:';
        const recordId = getField(Fields.currRecordId_delete);
        if (checkNullStr(recordId)) {
            updateField(Fields.recordDeleteBtn_Disable, true);
            updateField(Fields.recordDeleteBtn_AsLoading, true);
            try {
                const REQUEST_OPTIONS = {recordId: recordId};
                sendRequest({
                    reqUrl: ReqUrls.survey__mainPage_delete_record,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        updateField(Fields.showDeleteDialog, false)

                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');
                        if (success) {
                            toast(message, CssVariant.success);
                            // log(fun, 'getRecordList-old.before:', getArrLen(getRecordList()));


                            //Remove this recordId item from recordList
                            const recordList = getRecordList().filter((item) => recordId != getJsonValue(item, mf.recordId.column));
                            updateSessionStoreFieldValue(Fields.recordList, recordList);
                            // log(fun, 'getRecordList-after.length:', getArrLen(recordList));

                            if (!checkNullArr(recordList)) {
                                updateField(Fields.isDataFound, false);
                                updateField(Fields.dataLoadingMsg, "No records found!");
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        updateField(Fields.currRecordId_delete, null);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        toast("Couldn't delete the record, please try again!", CssVariant.danger);
                        updateField(Fields.currRecordId_delete, null);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                toast("Couldn't delete the record, please try again!", CssVariant.danger);
                updateField(Fields.currRecordId_delete, null);
                updateField(Fields.recordDeleteBtn_Disable, false);
                updateField(Fields.recordDeleteBtn_AsLoading, false);
            }
        } else {
            toast('Record-id not found!', CssVariant.danger);
        }
    }

    function handleDownloadRecordReport(recordId) {
        const fun = 'handleDownloadRecordReport:';
        if (checkNullStr(recordId)) {
            updateField(Fields.currRecordId_DlReport, recordId);
            updateField(Fields.recordDlReportBtn_Disable, true);
            updateField(Fields.recordDlReportBtn_AsLoading, true);
            try {
                const REQUEST_OPTIONS = {
                    recordId: recordId,
                    subdomainName: getSubdomainSessionField(ModuleFieldsData[Modules.Company].projectName.column),
                };
                sendRequest({
                    reqUrl: ReqUrls.survey__mainPage_download_survey_pdf,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);

                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');
                        if (success) {
                            toast(message, CssVariant.success);

                            const excelName = getJsonValueFromNestedKeys(fetchedData, ['data', 'excelName']);
                            const excelColumns = getJsonValueFromNestedKeys(fetchedData, ['data', 'excelColumns'], []);
                            const excelRows = getJsonValueFromNestedKeys(fetchedData, ['data', 'excelRows'], []);
                            log(fun, 'fetchedData.excelName:', excelName, 'excelColumns:', excelColumns, 'excelRows:', excelRows);

                            //download file
                            try {
                                downloadExcelFile({
                                    fileName: excelName,
                                    columns: excelColumns,
                                    rows: excelRows,
                                    designHeader: true,
                                });
                            } catch (error) {
                                logErr(fun, error);
                                toast("Couldn't download the report, please try again!", CssVariant.danger);
                                updateField(Fields.currRecordId_DlReport, null);
                                updateField(Fields.recordDlReportBtn_Disable, false);
                                updateField(Fields.recordDlReportBtn_AsLoading, false);
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        updateField(Fields.currRecordId_DlReport, null);
                        updateField(Fields.recordDlReportBtn_Disable, false);
                        updateField(Fields.recordDlReportBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        toast("Couldn't download the report, please try again!", CssVariant.danger);
                        updateField(Fields.currRecordId_DlReport, null);
                        updateField(Fields.recordDlReportBtn_Disable, false);
                        updateField(Fields.recordDlReportBtn_AsLoading, false);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                toast("Couldn't download the report, please try again!", CssVariant.danger);
                updateField(Fields.currRecordId_DlReport, null);
                updateField(Fields.recordDlReportBtn_Disable, false);
                updateField(Fields.recordDlReportBtn_AsLoading, false);
            }
        } else {
            toast('Record-id not found!', CssVariant.danger);
        }
    }

    //======================Request Functions end=====================


    //======================Util Functions start=====================
    function handleSearchInputChange(inputValue, searchByOption = getField(Fields.searchBySelectedOption)) {
        const fun = 'handleSearchInputChange:';
        try {
            updateField(Fields.searchInputValue, inputValue);
            const inputValueL = (inputValue + '').toLowerCase();
            let recordList = getRecordList();
            if (checkNullStr(inputValue) && checkNullArr(recordList)) {
                recordList = recordList.map((item) => {
                    const title = (getJsonValue(item, mf.title.column) + '').toLowerCase();
                    const introduction = (getJsonValue(item, mf.introduction.column) + '').toLowerCase();
                    const endDate = (formatDateValue(getJsonValue(item, mf.endDate.column)) + '').toLowerCase();

                    let matched = false;
                    switch (searchByOption) {
                        case SEARCH_BY_PARAMS.anything.name:
                            matched = (title + introduction + endDate).includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.title.name:
                            matched = title.includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.introduction.name:
                            matched = introduction.includes(inputValueL);
                            break;
                        case SEARCH_BY_PARAMS.date.name:
                            matched = endDate.includes(inputValueL);
                            break;
                    }
                    return {
                        ...item,
                        searched: matched
                    }
                });
            }

            // update-list
            startSearchListTransition(() => {
                updateSessionStoreFieldValue(Fields.recordList, recordList);
            });
        } catch (error) {
            logErr(fun, error);
            toast('Search failed, please try again', CssVariant.danger);
        }
    }

    function handleSortByChange(name, sortKey) {
        const fun = 'handleSortByChange:';
        try {
            startSortListTransition(() => {
                const sortOption = getField(Fields.sortBySelectedOption);
                const order = name == sortOption ? !isFieldTrue(Fields.isSortByOrderAsc) : isFieldTrue(Fields.isSortByOrderAsc);

                updateField(Fields.isSortByOrderAsc, order);
                updateField(Fields.sortBySelectedOption, name);

                updateSessionStoreFieldValue(Fields.recordList, sortJsonListAdvance(shuffleList(getRecordList()), sortKey, order));
            });
        } catch (error) {
            toast('Sort failed, please try again', CssVariant.danger);
            logErr(fun, error);
        }
    }

    function highlightSearchMatchedText(text, searchByParam) {
        const can = (getField(Fields.searchBySelectedOption) == SEARCH_BY_PARAMS.anything.name || getField(Fields.searchBySelectedOption) == searchByParam);
        return can ? getHighlightedMatchedText(text, getField(Fields.searchInputValue)) : text;
    }


    function getRecordList(categorized = false, searchedOnly = false) {
        if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
            return getList().filter(item => isJsonValueTrue(item, 'searched'))
        } else {
            return getList();
        }

        function getList() {
            if (checkNullArr(getJsonValue(sessionData, Fields.recordList))) {
                if (isBoolTrue(categorized) && getField(Fields.currEventTypeId) != EventTypes.all.id) {
                    return getJsonValue(sessionData, Fields.recordList)
                        // .filter(item => (getField(Fields.currEventTypeId) == EventTypes.expired.id ? isEventExpired(item) : !isEventExpired(item)));
                        .filter(item => {
                            const statusLabel = getEventStatusIndicatorTag(item).label;
                            switch (getField(Fields.currEventTypeId)) {
                                // case  EventTypes.ongoing.id:
                                //     return statusLabel == EventTypes.ongoing.text;
                                // case  EventTypes.today.id:
                                //     return statusLabel == EventTypes.today.text;
                                case  EventTypes.upcoming.id:
                                    return statusLabel == EventTypes.upcoming.text;
                                case  EventTypes.expired.id:
                                    return statusLabel == EventTypes.expired.text || statusLabel == EventTypes.expired.textAlt || statusLabel == EventTypes.expired.textAlt2;
                                default:
                                    return false;
                            }
                        });
                } else {
                    return getJsonValue(sessionData, Fields.recordList);
                }
            }
            return [];
        }
    }

    function getRecordListToRender(categorized = true) {
        return getRecordList(categorized, true).slice((getField(Fields.pagination_currPageNum) - 1) * getField(Fields.pagination_currPageLimit), getField(Fields.pagination_currPageNum) * getField(Fields.pagination_currPageLimit));
    }

    function isSearchOngoing() {
        return checkNullStr(getField(Fields.searchInputValue));
    }


    function getSummaryList() {
        return checkNullArr(getJsonValue(sessionData, Fields.summaryList)) ? getJsonValue(sessionData, Fields.summaryList) : [];
    }


    function isEventExpired(record = {}) {
        return isDateExpired(getJsonValue(record, mf.endDate.column));
    }

    /**
     * Checks the status of event-date & return appropriate status-label/status-indicator like ongoing/today/ended/expired etc.
     * @param record{Object} - Object from which date is to be extracted.
     * @return {Object} - Object containing "tag", "bg" and "color" keys with their values.
     */
    function getEventStatusIndicatorTag(record) {
        const startDate = getJsonValue(record, mf.startDate.column);
        const endDate = getJsonValue(record, mf.endDate.column);
        if (isDateExpired(endDate)) {
            return {label: EventTypes.expired.text, bg: 'var(--danger15)', color: 'var(--danger)'};
            // } else if (isCurrentDateTimeInRange(startDate, endDate) && isDateInCurrentPeriod(startDate, 'day')) {
            //     return {label: EventTypes.today.text, bg: theme.primaryAltDark10, color: theme.primaryAltDark};
            // } else if (isCurrentDateTimeInRange(startDate, endDate)) {
            //     return {label: EventTypes.ongoing.text, bg: theme.primaryAlt10, color: theme.primaryAlt};
        } else if (!isDateExpired(endDate)) {
            return {label: EventTypes.upcoming.text, bg: theme.primaryBlue10, color: theme.primaryBlue};
        } else {
            return {label: "", bg: 'var(--danger20)', color: 'var(--danger)'};
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }


    //======================Util Functions end=====================
});
export default SurveyPage;
